import { useState } from "react";
import { isAxiosError } from "axios";
import { MuiTable, TableCell } from "src/components/MuiTable/MuiTable.tsx";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type {
  StoreSupplierAgreementPriceRequestBody,
  SupplierAgreementPriceResource,
} from "@exonn/js-sdk";
import { formatDate } from "@exonn/utils";
import { Panel } from "src/components/Panel/Panel.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { useErpMutation } from "src/system/useErpMutation.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { useResourceFormApi } from "src/components/Form/hooks/useResourceFormApi.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import type { SupplierAgreementFormData } from "src/admin/modules/supplier-agreement/forms/supplier-agreement/config.tsx";
import {
  PriceDialog,
  type PriceDialogMode,
} from "src/admin/modules/supplier-agreement/forms/supplier-agreement/tabs/prices/PriceDialog.tsx";

type DialogState =
  | { mode: "create" }
  | { mode: "close"; price: SupplierAgreementPriceResource }
  | null;

const statusOf = (price: SupplierAgreementPriceResource) => {
  if (price.is_editable) {
    return "future";
  }
  return price.is_valid_today ? "current" : "expired";
};

// Ступени цен соглашения с историей. Действующие цены не меняются — только закрываются;
// будущие можно удалить. Правила проверяет бэкенд (SupplierAgreementPriceService).
export const AgreementPrices = () => {
  const { t } = useResourceFormTranslation();
  const data = useResourceFormData<SupplierAgreementFormData>();
  const formApi = useResourceFormApi();
  const exonnApi = useExonnApi();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const agreement = data?.data;

  const onDone = () => {
    setDialog(null);
    setErrors({});
    formApi.refresh({ revalidateData: true, resetForm: false });
  };
  const onError = (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 422) {
      setErrors(error.response.data?.errors ?? {});
    }
  };

  const { mutate: save, isPending: isSaving } = useErpMutation({
    mutationKey: [ResourceEnum.SupplierAgreement, agreement?.id, "prices"],
    mutationFn: (body: Partial<StoreSupplierAgreementPriceRequestBody>) => {
      if (dialog?.mode === "close") {
        return exonnApi.client.put(
          `/supplier-agreement-price/${dialog.price.id}`,
          body,
        );
      }
      return exonnApi.client.post(
        `/supplier-agreement/${agreement?.id}/prices`,
        body,
      );
    },
    errorMessage: t("prices.notifications.error"),
  });

  const { mutate: remove } = useErpMutation({
    mutationKey: [ResourceEnum.SupplierAgreement, agreement?.id, "prices"],
    mutationFn: (price: SupplierAgreementPriceResource) =>
      exonnApi.client.delete(`/supplier-agreement-price/${price.id}`),
    errorMessage: t("prices.notifications.error"),
  });

  if (!agreement) {
    return null;
  }

  const prices = agreement.prices ?? [];
  const currency = agreement.currency?.iso ?? "";

  return (
    <div style={{ padding: 15 }}>
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("prices.title")}
          </Panel.Title>
          <Button
            size={"sm"}
            variant={"outlined"}
            onClick={() => setDialog({ mode: "create" })}
          >
            {t("prices.add")}
          </Button>
        </Panel.Header>
        <Panel.Content>
          {prices.length === 0 ? (
            <TextV2 variant={"body1"}>{t("prices.empty")}</TextV2>
          ) : (
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("prices.min-quantity")}</TableCell>
                  <TableCell>{t("prices.unit-price")}</TableCell>
                  <TableCell>{t("prices.valid-from")}</TableCell>
                  <TableCell>{t("prices.valid-to")}</TableCell>
                  <TableCell>{t("prices.status.label")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {prices.map((price) => {
                  const status = statusOf(price);
                  return (
                    <TableRow key={price.id}>
                      <TableCell>{Number(price.min_quantity)}</TableCell>
                      <TableCell>
                        {Number(price.unit_price)} {currency}
                      </TableCell>
                      <TableCell>{formatDate(price.valid_from)}</TableCell>
                      <TableCell>
                        {price.valid_to ? formatDate(price.valid_to) : "—"}
                      </TableCell>
                      <TableCell>{t(`prices.status.${status}`)}</TableCell>
                      <TableCell align={"right"}>
                        {status === "current" && (
                          <Button
                            size={"sm"}
                            variant={"outlined"}
                            onClick={() => setDialog({ mode: "close", price })}
                          >
                            {t("prices.close")}
                          </Button>
                        )}
                        {status === "future" && (
                          <Button
                            size={"sm"}
                            variant={"outlined"}
                            skin={"danger"}
                            onClick={() => remove(price, { onSuccess: onDone })}
                          >
                            {t("prices.delete")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          )}
        </Panel.Content>
      </Panel>
      {dialog && (
        <PriceDialog
          mode={dialog.mode as PriceDialogMode}
          isSaving={isSaving}
          errors={errors}
          onSubmit={(body) => save(body, { onSuccess: onDone, onError })}
          onClose={() => {
            setDialog(null);
            setErrors({});
          }}
        />
      )}
    </div>
  );
};
